<template>
	<div class="flex flex-col gap-6 p-6 @container h-full">
		<div class="mb-8">
			<h2 class="mb-6 text-lg border-b">{{ $t('Actions') }}</h2>
			<div class="grid grid-cols-3 gap-2 mb-4">
				<div v-if="!fullscreen" class="flex flex-col items-center gap-2 text-[.7rem] text-center">
					<button class="btn btn-circle btn-lg bg-base-100" @click="activateFullscreen">
						<ArrowsPointingOutIcon class="w-10 h-10"/>
					</button>
					{{ $t('Fullscreen') }}
				</div>
				<div v-else class="flex flex-col items-center gap-2 text-[.7rem] text-center">
					<button class="btn btn-circle btn-lg bg-base-100" @click="deactivateFullscreen">
						<ArrowsPointingInIcon class="w-10 h-10"/>
					</button>
					{{ $t('Exit fullscreen mode') }}
				</div>
				<div v-if="!activityBoardStore.activitiesPaused" class="flex flex-col items-center gap-2 text-[.7rem] text-center">
					<button class="btn btn-circle btn-lg bg-base-100" @click="setActivitiesPause(true)" :disabled="togglingPause || restarting">
						<span v-show="togglingPause" class="loading loading-spinner absolute"></span>
						<PauseIcon class="w-10 h-10" :class="[togglingPause? 'hidden' : '']"/>
					</button>
					{{ $t('Pause') }}
				</div>
				<div v-else class="flex flex-col items-center gap-2 text-[.7rem] text-center">
					<button class="btn btn-circle btn-lg bg-base-100" @click="setActivitiesPause(false)" :disabled="togglingPause || restarting">
						<span v-show="togglingPause" class="loading loading-spinner absolute"></span>
						<PlayIcon class="w-10 h-10" :class="[togglingPause? 'hidden' : '']" />
					</button>
					{{ $t('Resume') }}
				</div>
				<div class="flex flex-col items-center gap-2 text-center">
					<Dropdown position="bottom-end">
						<template #trigger>
						<button class="btn btn-circle btn-lg bg-base-100" :disabled="restarting || togglingPause">
							<span v-show="restarting" class="loading loading-spinner absolute"></span>
							<!-- phosphor icon - arrow arc left -->
							<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" fill="#000000" viewBox="0 0 256 256" :class="[restarting? 'hidden' : '']">
								<path d="M232,184a8,8,0,0,1-16,0A88,88,0,0,0,65.78,121.78L43.4,144H88a8,8,0,0,1,0,16H24a8,8,0,0,1-8-8V88a8,8,0,0,1,16,0v44.77l22.48-22.33A104,104,0,0,1,232,184Z">
								</path>
							</svg>
						</button>
						</template>
						<template #content="dialogActions">
							<DropdownDialogConfirm :confirm-label="$t('Yes')" :cancel-label="$t('No')" confirm-style="danger" class="w-96" @cancel="dialogActions.close" @confirm="confirmResetActivityBoard(dialogActions)">
								<span class="text-danger">{{ $t('Are you sure you want to remove all children from their activities and start again?') }}</span>
							</DropdownDialogConfirm>
						</template>
					</Dropdown>
					<span class="text-[.7rem]">{{ $t('Restart') }}</span>
				</div>
			</div>

			<!-- <div class="flex justify-center"> -->
				<Dropdown :visible="showClosingBoardConfirmation" position="bottom-start" wrapperClasses="relative w-full flex justify-center">
					<template #trigger>
						<button class="btn btn-error mt-4" @click="initializeCloseBoardTime">
							<NoSymbolIcon class="w-5 h-5 mr-2" />
							{{ $t('Close the activity board') }}
						</button>
					</template>
					<template #content="dialogActions">
						<div class="flex justify-between items-center p-4 rounded-md bg-base shadow-lg ring-1 ring-neutral ring-opacity-5 focus:outline-none">
							<div>
								<div class="grid grid-cols-1 divide-y divide-base-300">
									<div class="@sm:py-5 py-2">
										<div class="font-bold text-lg">{{ $t('End the activity board & all activities') }}</div>
										<span class="block mb-2">{{ $t('and sign off pupils at') }}...</span>
										<div class="flex items-end justify-between ">
											<div>
												<form class="">
													<input type="number" v-model="closeBoardTime.hours" min="0" max="23" required class="input" /> :
													<input type="number" v-model="closeBoardTime.mins" min="0" max="23" required class="input" />
													<p v-if="!validClosingBoardTime" class="text-red-600 text-sm mt-2 text-center h-5">{{ $t('Invalid time') }}</p>
												</form>
											</div>

											<button type="button" class="btn btn-danger" @click="handleBoardClosing(dialogActions, true)">
												{{ $t('Confirm') }}
											</button>
										</div>
										<p class="alert alert-warning text-sm py-1 mt-3">
											<ExclamationTriangleIcon class="w-5 h-5 shrink-0" />
											{{ $t('This will also end the activity board on other active devices currently in use.') }}
										</p>
									</div>
									<div class="@sm:py-5 py-2 flex items-center justify-between gap-4">
										<div>
											<div class="font-bold text-lg">{{ $t('Leave the activity board on this device') }}</div>
											<span>{{ $t('without ending the activities') }}</span>
										</div>
										<button type="button" class="btn btn-neutral" @click="handleBoardClosing(dialogActions, false)">
											{{ $t('Confirm') }}
										</button>
									</div>
									<div class="@sm:py-5 py-2 flex justify-end">
										<button type="button" class="btn" @click="dialogActions.close">
											{{ $t('Cancel') }}
										</button>
									</div>
								</div>
							</div>
						</div>
					</template>
				</Dropdown>
			<!-- </div> -->
		</div>
		<div >
			<h2 class="mb-6 text-lg border-b">{{ $t('Pupil feedback options') }}</h2>
			<div class="flex flex-col">
				<ActivityBoardPupilFeedbackOptions />
			</div>
		</div>
		<div>
			<h2 class="mb-6 text-lg border-b">{{ $t('Avatar display') }}</h2>
			<div class="flex flex-col gap-8">
				<div class="flex flex-col gap-2">
					<label class="flex items-center gap-3">
						<input type="radio" name="avatar-format" class="radio"
							:checked="activityBoardStore.avatarWithSymbol"
							@click="setAvatarLook(true, false)"
						/>
						<span>{{ $t('Show symbol only') }}</span>
					</label>
					<label class="flex items-center gap-3">
						<input type="radio" name="avatar-format" class="radio"
							:checked="activityBoardStore.avatarWithName"
							@click="setAvatarLook(false, true)"
						/>
						<span>{{ $t('Show name only') }}</span>
					</label>
					<label class="flex items-center gap-3">
						<input type="radio" name="avatar-format" class="radio"
							:checked="activityBoardStore.avatarWithSymbol && activityBoardStore.avatarWithName"
							@click="setAvatarLook(true, true)"
						/>
						<span>{{ $t('Show both name and symbol') }}</span>
					</label>
				</div>
				<div class="flex flex-col items-start">
					<span class="">{{ $t('Show symbols with a specific color only') }}:</span>
					<ActivityBoardAvatarColorFilter class="mt-4" />
				</div>
				<label class="flex items-center">
					<input type="checkbox" class="checkbox" name="color-filter-display"
						:checked="activityBoardStore.avatarColorFilterEnabled"
						@click="enableAvatarColorFilter($event.target.checked)"
					/>
					<span class="ml-3">{{ $t('Allow pupils to filter by symbol color') }}</span>
				</label>
			</div>
		</div>
		<div class="mt-auto">
			<!-- divider -->
			<div class="divider opacity-50 -mx-6"></div>

			<div class="flex items-center gap-2">
				<div class="text-sm">
					{{ $t('Are you having trouble with the activity board? Reloading the page might help.') }}
				</div>
				<button class="btn rounded bg-base-100 w-16" @click="$emit('requestReloadBoard')">
					<ArrowPathIcon class="w-6 h-6"/>
				</button>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
import { useActivityBoardStore } from "@/stores/ActivityBoard.store";
import { mapStores } from "pinia";

import { DateTime } from "luxon";
import Dropdown from "@/components/ui/Dropdown.vue";
import DropdownDialogConfirm from "@/components/ui/DropdownDialogConfirm.vue";
import ActivityBoardAvatarColorFilter from "@/components/ActivityBoardAvatarColorFilter.vue";
import ActivityBoardPupilFeedbackOptions from "@/components/ActivityBoardPupilFeedbackOptions.vue";
import { ArrowPathIcon, PauseIcon, PlayIcon, NoSymbolIcon, ArrowsPointingInIcon, ArrowsPointingOutIcon, ExclamationTriangleIcon } from "@heroicons/vue/24/outline";

export default {
	emits: [
		'requestReloadBoard',
		'requestCloseBoard',
	],
	components: {
		Dropdown, DropdownDialogConfirm, ActivityBoardAvatarColorFilter, ActivityBoardPupilFeedbackOptions,
		ArrowPathIcon, PauseIcon, PlayIcon, NoSymbolIcon, ArrowsPointingInIcon, ArrowsPointingOutIcon, ExclamationTriangleIcon
	},
	data() {
		return {
			fullscreen: false,
			restarting: false,
			togglingPause: false,
			showClosingBoardConfirmation: false,
			closeBoardTime: {
				hours: 0,
				mins: 0,
			},
		};
	},
	computed: {
		...mapStores(useActivityBoardStore),

		validClosingBoardTime() {
			return this.closeBoardTime.hours
				&& this.closeBoardTime.mins
				&& isNaN(this.closeBoardTime.hours) === false
				&& isNaN(this.closeBoardTime.mins) ===  false
				&& this.closeBoardTime.hours >= 0
				&& this.closeBoardTime.hours <= 23
				&& this.closeBoardTime.mins >= 0
				&& this.closeBoardTime.mins <= 59;
		}
	},
	beforeMount() {
		// toggle this.fullscreen this way to also handle the case where the user leaves fullscreen mode by pressing ESC
		document.documentElement.addEventListener('fullscreenchange', () => {
			this.fullscreen = document.fullscreenElement !== null;
		});
	},
	mounted() {
		this.fullscreen = document.fullscreenElement !== null;
	},
	methods: {
		setAvatarLook(withSymbol: Boolean, withName: Boolean) {
			this.activityBoardStore.setAvatarLook(withSymbol, withName);
			if(this.$gtag) {
				this.$gtag.event('change-avatar-look', {
					'event_category': 'activity-board',
					'event_label': 'configuration',
					'value': (withSymbol ? 'symbol' : '') + (withName ? 'name' : '')
				});
			}
		},

		async setActivitiesPause(paused: Boolean) {
			this.togglingPause = true;
			try {
				if(paused) {
					await this.activityBoardStore.pauseActivities();
				} else {
					await this.activityBoardStore.resumeActivities();
				}
			} catch (e) {

			} finally {
				this.togglingPause = false;
				if(this.$gtag) {
					this.$gtag.event('pause-activities', {
						'event_category': 'activity-board',
						'event_label': 'configuration',
						'value': paused ? 'on' : 'off'
					});
				}
			}
		},

		enableAvatarColorFilter(enabled: Boolean) {
			this.activityBoardStore.enableAvatarColorFilter(enabled);
			if(this.$gtag) {
				this.$gtag.event('color-filter-toggling', {
					'event_category': 'activity-board',
					'event_label': 'configuration',
					'value': enabled ? 'on' : 'off'
				});
			}
		},

		async confirmResetActivityBoard(dialogActions) {

			dialogActions.close();

			this.restarting = true;
			try {
				await this.activityBoardStore.restartBoard();
				if(this.activityBoardStore.activitiesPaused) {
					await this.activityBoardStore.resumeActivities();
				}
			} catch (e) {
				this.$notify({ text: this.$t('An error occurred'), type: 'error' });
			} finally {
				this.restarting = false;
			}
		},

		activateFullscreen() {
			const element = document.documentElement;
			if(element.requestFullscreen) {
				element.requestFullscreen();        // W3C spec
			}
			else if (element.mozRequestFullScreen) {
				element.mozRequestFullScreen();     // Firefox
			}
			else if (element.webkitRequestFullscreen) {
				element.webkitRequestFullscreen();  // Safari
			}
			else if(element.msRequestFullscreen) {
				element.msRequestFullscreen();      // IE/Edge
			}

			if(this.$ua) {
				this.$ua.trackEvent(
					'activity-board', 'configuration', 'fullscreen', 'on'
				);
			}
		},

		deactivateFullscreen() {
			if(document.exitFullscreen) {
				document.exitFullscreen();
			} else if (document.mozCancelFullScreen) {
				document.mozCancelFullScreen();
			} else if (document.webkitExitFullscreen) {
				document.webkitExitFullscreen();
			}

			if(this.$ua) {
				this.$ua.trackEvent(
					'activity-board', 'configuration', 'fullscreen', 'off'
				);
			}
		},

		initializeCloseBoardTime() {
			// add 1 minute to avoid pupils getting checked out at a time BEFORE check-in time
			const now = DateTime.local().plus({ minutes: 1 });
			[ this.closeBoardTime.hours, this.closeBoardTime.mins ] = now.toLocaleString(DateTime.TIME_24_SIMPLE).split(':');
		},

		handleBoardClosing(dialogActions, endActivities: Boolean = false) {

			if(!this.validClosingBoardTime) {
				return;
			}

			dialogActions.close();

			if(endActivities) {
				this.$emit('requestCloseBoard', DateTime.fromObject({
					hour: this.closeBoardTime.hours,
					minute: this.closeBoardTime.mins,
				}));
			} else {
				this.$emit('requestCloseBoard', null);
			}
		},
	}
}
</script>
